import { useCallback, useEffect, useRef, useState } from "react";
import { isValidIranianMobile, type CurrentUser } from "@talent/protocol";
import { AuthError, fetchMe, requestCode, verifyCode, type AuthFailure } from "../auth-api.js";
import { COPY, fill } from "../copy.js";
import { digitsOnly, fa } from "../format.js";
import {
  AuthCard,
  CODE_LENGTH,
  CodeBoxes,
  FIELD,
  LABEL,
  LINK,
  PRIMARY,
} from "./auth-parts.js";

/** The account the shell is handed on success. */
type SignedInUser = CurrentUser;

/**
 * Sign in by SMS code.
 *
 * Two steps — the number, then the code — and then one of four endings. **A correct code is no longer
 * the same thing as an account** (ADR-012): a teacher exists once their application is approved and a
 * child exists once their teacher adds them to a class, so three of the four endings are screens rather
 * than sessions.
 *
 * Those three are deliberately not errors. Somebody waiting on a decision typed the right six digits;
 * showing them "this code is not valid" would send them to retype it forever. What each screen has to
 * carry instead is who can do something about it — the teacher adds a child's number, the queue is read
 * by a person, a refusal can be appealed by applying again.
 */

const FAILURE_COPY: Record<AuthFailure, string> = {
  "bad-phone": COPY.enter.badPhone,
  "bad-code": COPY.enter.badCode,
  cooldown: COPY.enter.cooldown,
  quota: COPY.enter.quota,
  "sms-failed": COPY.enter.smsFailed,
  deleted: COPY.enter.deleted,
  // Reachable only from the application form, never from here — but the map is total, so a code the
  // server adds later cannot become a blank screen.
  "already-applied": COPY.apply.alreadyApplied,
  "national-id-taken": COPY.apply.nationalIdTaken,
  unknown: COPY.enter.failed,
};

/** Ticks a countdown down to zero. Returns the remaining seconds. */
function useCountdown(from: number): [number, (seconds: number) => void] {
  const [remaining, setRemaining] = useState(from);

  useEffect(() => {
    if (remaining <= 0) return;
    // A one-second interval rather than a timeout chain, so a re-render mid-tick does not
    // reset the countdown and leave the button disabled longer than the server requires.
    const id = setInterval(() => setRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [remaining]);

  return [remaining, setRemaining];
}

/** Which of the three no-account endings is being shown, if any. */
type Ending =
  | { kind: "not-registered" }
  | { kind: "application-pending" }
  | { kind: "application-rejected"; note: string | null };

export function AuthScreen({
  onSignedIn,
  onApply,
}: {
  /**
   * Handed the account that was just signed in.
   *
   * The role comes back with the verification, and passing it up is what lets the shell send a
   * teacher to their class and a child to the games without a second request or an intermediate
   * screen (ADR-011).
   */
  onSignedIn: (user: SignedInUser) => void;
  /**
   * To the registration page.
   *
   * Reached from the endings below and from nowhere else on this screen: a teacher who has typed a
   * correct code on an unknown number has just been told there is no account, and leaving them there
   * with no way forward would be the one thing worse than the link that used to sit on the form.
   */
  onApply: () => void;
}) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [ending, setEnding] = useState<Ending | null>(null);
  const [failure, setFailure] = useState<AuthFailure | null>(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useCountdown(0);

  /** Maps a thrown error onto something sayable. Anything unrecognised stays unknown. */
  const handleError = useCallback(
    (err: unknown) => {
      if (err instanceof AuthError) {
        setFailure(err.failure);
        if (err.retryAfterSeconds) setCooldown(err.retryAfterSeconds);
        return;
      }
      setFailure("unknown");
    },
    [setCooldown],
  );

  const askForCode = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      // Guarded here too, not only on the button: a text field submits on Enter regardless of
      // whether the button below it is disabled, so an empty or malformed number would otherwise
      // still reach the server and spend a round trip on a refusal it could have skipped.
      if (busy || !isValidIranianMobile(phone)) return;
      setBusy(true);
      setFailure(null);
      try {
        const { retryAfterSeconds } = await requestCode(phone);
        setCooldown(retryAfterSeconds);
        setStep("code");
      } catch (err) {
        handleError(err);
      } finally {
        setBusy(false);
      }
    },
    [busy, handleError, phone, setCooldown],
  );

  const startOver = useCallback(() => {
    setStep("phone");
    setCode("");
    setEnding(null);
    setFailure(null);
  }, []);

  const submitCode = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (busy) return;
      setBusy(true);
      setFailure(null);
      try {
        const outcome = await verifyCode(phone, code);
        if (outcome.kind !== "signed-in") {
          // Not an error, so the code is left alone and the field is not cleared — there is nothing
          // for them to retype. The ending screen replaces the form entirely.
          setEnding(outcome);
          return;
        }
        /**
         * The learner record is fetched here rather than guessed.
         *
         * A student's device needs it before the first event can be recorded. `verify` returns the
         * account already, so this is a fallback for the case where the cookie was set but the body
         * was not what this build expects — one extra call at the one moment where a round trip is
         * expected anyway, rather than a play surface with nowhere to write.
         */
        onSignedIn(outcome.user ?? ((await fetchMe()) as SignedInUser));
      } catch (err) {
        // The code is cleared on failure. Leaving a rejected code in the field invites another
        // press of the button on the same wrong value.
        setCode("");
        handleError(err);
      } finally {
        setBusy(false);
      }
    },
    [busy, code, handleError, onSignedIn, phone],
  );

  const error = failure && (
    <p className="mt-md text-small text-wrong">{FAILURE_COPY[failure]}</p>
  );

  /**
   * A full code submits itself.
   *
   * The last digit of a six-digit code is unambiguous — there is nothing else the person could mean —
   * so asking them to reach for a button afterwards is a step with no decision in it. It also matters
   * on the platform where the code arrives by autofill: the field fills in one go, and without this the
   * screen sits there looking finished and doing nothing.
   *
   * The guard is a ref reset whenever the field is not full, rather than a check against the code
   * itself. Guarding on the value would block a legitimate second attempt at the same digits — which
   * happens, because a rejected code is cleared and a person retypes what they read.
   *
   * At the top level rather than beside the second step's markup: a hook after an early return is not
   * a hook React can rely on, and the endings below return early.
   */
  const autoSubmitted = useRef(false);
  useEffect(() => {
    if (step !== "code" || ending) return;
    if (code.length < CODE_LENGTH) {
      autoSubmitted.current = false;
      return;
    }
    if (busy || autoSubmitted.current) return;
    autoSubmitted.current = true;
    void submitCode();
  }, [code, busy, ending, step, submitCode]);

  if (ending) {
    return <NoAccount ending={ending} onApply={onApply} onRetry={startOver} />;
  }

  if (step === "phone") {
    const cannotAsk = busy || !isValidIranianMobile(phone);
    return (
      <AuthCard step={1} icon="phone" title={COPY.enter.title} intro={COPY.enter.intro}>
        <form onSubmit={askForCode}>
          <div className="mt-lg">
            <label className={LABEL} htmlFor="phone">
              {COPY.enter.phoneLabel}
            </label>
            <input
              id="phone"
              value={phone}
              /**
               * Normalised as it is typed, so a Persian keyboard works.
               *
               * `digitsOnly` folds ۰-۹ and ٠-٩ to Latin and drops spaces and dashes — which also means a
               * number pasted as "0912 345 6789" is accepted rather than refused for its formatting.
               * `domain/phone.ts` still has the last word on what a valid number is.
               */
              onChange={(e) => setPhone(digitsOnly(e.target.value).slice(0, 13))}
              inputMode="numeric"
              // An example of the shape wanted. Written in Persian digits, which is what the field
              // will be filled with — a Latin example next to a Persian keyboard reads as a mismatch.
              placeholder={COPY.enter.phonePlaceholder}
              autoComplete="tel"
              dir="ltr"
              className={`${FIELD} text-center text-title tracking-[2px]`}
            />
          </div>

          {error}

          <button
            type="submit"
            disabled={cannotAsk}
            className={`${PRIMARY} ${cannotAsk ? "opacity-60" : ""}`}
          >
            {busy ? COPY.enter.working : COPY.enter.sendCode}
          </button>
        </form>

        {/*
          * Why a five-year-old is being asked for a phone number.
          *
          * The line was written when the sign-in flow was chosen and then never rendered. It belongs on
          * the first step, where an adult is looking at a form that seems to be for them and is in fact
          * how their child reaches the games — the weakest part of the design, so the least it can do is
          * explain itself (ADR-011).
          */}
        <p className="mt-md text-caption text-muted">{COPY.enter.childNote}</p>

        {/*
          * **No link to registering here, deliberately.**
          *
          * There was one, and it put a five-field teacher form one tap under a phone box that the parent
          * of a five-year-old is the commonest visitor to — which made the way in read as a registration
          * wall to the people with nothing to register. Registering has its own door now, in the header
          * and on the landing page, and it is still offered on the two endings below where somebody has
          * proved the number and turned out to have no account. Those are answers to a question this
          * screen asked; this is not.
          */}
      </AuthCard>
    );
  }

  const cannotSubmit = busy || code.length !== CODE_LENGTH;

  return (
    <AuthCard
      step={2}
      icon="code"
      title={COPY.enter.codeTitle}
      intro={fill(COPY.enter.codeSent, { phone })}
    >
      <form onSubmit={submitCode}>
        <div className="mt-lg">
          <label className={LABEL} htmlFor="code">
            {COPY.enter.codeLabel}
          </label>
          <CodeBoxes value={code} onChange={setCode} />
        </div>

        {error}

        <button
          type="submit"
          disabled={cannotSubmit}
          className={`${PRIMARY} ${cannotSubmit ? "opacity-60" : ""}`}
        >
          {busy ? COPY.enter.working : COPY.enter.verify}
        </button>

        <button
          type="button"
          disabled={cooldown > 0 || busy}
          onClick={() => void askForCode()}
          className={`${LINK} ${cooldown > 0 || busy ? "opacity-60" : ""}`}
        >
          {cooldown > 0
            ? fill(COPY.enter.resendIn, { seconds: fa(cooldown) })
            : COPY.enter.resend}
        </button>

        <button type="button" onClick={startOver} className={LINK}>
          {COPY.enter.changeNumber}
        </button>
      </form>
    </AuthCard>
  );
}

/**
 * The code was right and there is no account. Which of the three it is decides what to offer.
 *
 * One component rather than three, because the shape is identical and only the sentence and the action
 * differ — and because the thing they have in common is the part that matters: none of them blames the
 * person for the code.
 */
function NoAccount({
  ending,
  onApply,
  onRetry,
}: {
  ending: Ending;
  onApply: () => void;
  onRetry: () => void;
}) {
  if (ending.kind === "application-pending") {
    return (
      <AuthCard steps={1} step={1} icon="done" title={COPY.enter.pending.title}>
        <p className="mt-md text-body">{COPY.enter.pending.body}</p>
        <p className="mt-sm text-caption text-muted">{COPY.enter.pending.wait}</p>
        <button type="button" onClick={onRetry} className={LINK}>
          {COPY.enter.notRegistered.retry}
        </button>
      </AuthCard>
    );
  }

  if (ending.kind === "application-rejected") {
    return (
      <AuthCard steps={1} step={1} icon="form" title={COPY.enter.rejected.title}>
        <p className="mt-md text-body">{COPY.enter.rejected.body}</p>
        {/* The decider's own words, when they left any. A refusal with no reason cannot be answered. */}
        {ending.note && (
          <p className="mt-sm rounded-md bg-cream px-md py-sm text-small">
            {fill(COPY.enter.rejected.reason, { note: ending.note })}
          </p>
        )}
        <button type="button" onClick={onApply} className={PRIMARY}>
          {COPY.enter.rejected.again}
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard steps={1} step={1} icon="form" title={COPY.enter.notRegistered.title}>
      {/*
        * The parent's sentence first, because the parent of a five-year-old is who mostly lands here —
        * their teacher has not added the number yet, and nothing they do on this screen can fix that.
        */}
      <p className="mt-md text-body">{COPY.enter.notRegistered.childBody}</p>
      <p className="mt-md text-small text-muted">{COPY.enter.notRegistered.teacherBody}</p>
      <button type="button" onClick={onApply} className={PRIMARY}>
        {COPY.enter.notRegistered.apply}
      </button>
      <button type="button" onClick={onRetry} className={LINK}>
        {COPY.enter.notRegistered.retry}
      </button>
    </AuthCard>
  );
}
